<template>
  <div style="padding:0; position:relative; height:100%; width:100%; flex:1;">
    <div class="outer-new-request-panel">
    <div class="new-request-panel" style="position:absolute; top:24px; z-index:10;">
      <div class="new-request-panel-content">
        <!-- Once the order exists, the panel BECOMES the confirmation rather
             than the route changing underneath it. The form is kept mounted
             (v-show) so the map markers and route it owns stay on screen
             through the hold — unmounting it would blank the map a beat
             before tracking opens, which is exactly the flicker this
             replaces. -->
        <OrderPlacedCard
          v-if="placedOrderNumber"
          :order-number="placedOrderNumber"
          :notice="placedNotice"
          :booked-for="placedBookedFor"
          :hold-ms="placedNotice ? 6000 : (placedBookedFor ? 4000 : 1500)"
          @done="goToTracking"
        />
        <div v-show="!placedOrderNumber">
        <!-- Details step: after Continue. The stops and prices stay mounted
             underneath (v-show) so going back keeps them as they were. -->
        <OrderDetailsStep
          v-if="detailsOpen && selectedQuote"
          :vehicle-name="selectedQuote.vehicle_name"
          :total="orderTotal"
          :locations="formInputs.locations"
          v-model:goods="goodsDescription"
          v-model:pickup-mode="pickupMode"
          v-model:pickup-time="pickupTime"
          v-model:notes="formNotes"
          v-model:manifest="formManifestNumber"
          :now="nowTick"
          :placing="order_loading"
          :placed="!!placedOrderNumber"
          :dedicated="orderType === 'dedicated'"
          :loaders-offer="loadersOffer"
          v-model:loaders-count="loadersCount"
          :preferred-driver-enabled="ORDER_FEATURES.preferredDriver"
          v-model:preferred-driver="preferredDriver"
          :vehicle-type-id="selectedQuote.vehicle_type_id"
          @back="detailsOpen = false"
          @edit-stops="editStops"
          @save-stop="saveStopDetails"
          @confirm="placeOrder"
        />
        <div v-show="!detailsOpen">
        <div class="new-request-head">
          <!-- On-Demand / Dedicated. Dedicated shows only for companies
               with dedicated_orders on (utils/orderFeatures). -->
          <el-row>
            <el-col :span="2"></el-col>
            <el-col :span="20">
              <OrderTypeTabs v-model="orderType" :show-dedicated="showDedicatedTab" />
            </el-col>
            <el-col :span="2"></el-col>
          </el-row>
          <el-scrollbar max-height="220px">
            <template v-for="(location, index) in formInputs.locations" :key="location.uid">
              <el-row
                class="new-request-location-row-breather__minimal"
                v-if="index == 0"
              ></el-row>
              <el-row>
                <el-col :span="2"
                  ><el-icon
                    size="small"
                    class="new-request-location-icon"
                    style="display: none"
                    :class="{
                      'new-request-pickup-location-icon': isNewLocationPickupIcon(index),
                      'new-request-delivery-location-icon': isNewLocationDeliveryIcon(index),
                    }"
                    ><MapLocation /></el-icon
                ></el-col>
                <el-col :span="20"
                  ><AddressCombobox
                    v-model="location.name"
                    :suggestions="addressSuggestions"
                    :stop-index="index"
                    :placeholder="setPlaceHolderName(index)"
                    :input-class="'request-stop'"
                    @resolved="applyLocation(index, $event)"
                    @favourite="pinAddress($event, true)"
                    @unfavourite="pinAddress($event, false)"
                  />
                </el-col>
                <el-col :span="2" v-show="displayDeleteIcon(index)">
                  <el-tooltip content="Delete this location" placement="top">
                    <el-icon
                      size="small"
                      @click="removeLocation(index)"
                      class="new-request-location-icon new-request-location-icon__actionable"
                      ><Delete /></el-icon
                  ></el-tooltip>
                </el-col>
              </el-row>
              <el-row class="new-request-location-row-breather"></el-row>
            </template>

            <div v-if="allowNewLocation">
              <el-row>
                <el-col :span="2"></el-col>
                <el-col :span="20">
                  <span class="new-request-add-destination-button" @click="addNewDestination">
                    <a class="add-destination"> <strong> + </strong> Add Destination</a>
                  </span>
                </el-col>
                <el-col :span="2"></el-col>
              </el-row>
            </div>
            <!-- At the account's limit Add Destination just goes. The limit
                 is set by Xobo per customer and is not shown to customers. -->
          </el-scrollbar>
        </div>
        <el-divider />

        <Transition name="panel-slide">
        <div class="new-request-body-section">
          <!-- Price quote limit: a warning while using the last few quotes,
               then when quoting resumes (pricing helpers/priceQuoteLimit.js). -->
          <el-row v-if="quoteNotice">
            <el-col :span="2"></el-col>
            <el-col :span="20">
              <p class="quote-limit-note" :class="`is-${quoteNotice.kind}`" role="status">
                <strong>{{ quoteNotice.title }}</strong>
                {{ quoteNotice.text }}
              </p>
            </el-col>
            <el-col :span="2"></el-col>
          </el-row>
          <div v-if="showMinimisedBody">
            <el-row v-if="routeIsApproximate">
              <el-col :span="2"></el-col>
              <el-col :span="20">
                <p class="route-approx-note">
                  We couldn't find a driving route between these points, so the
                  line shown is direct rather than by road. This usually means
                  one of them is a broad area rather than a specific place —
                  picking a town or street instead normally fixes it, and
                  pricing needs a routable address too.
                </p>
              </el-col>
              <el-col :span="2"></el-col>
            </el-row>
            <el-row v-if="isValidForm">
              <el-col :span="2"></el-col>
              <el-col :span="20">
                <el-tooltip
                  class="box-item"
                  effect="dark"
                  content="The bubble indicates the total number of locations"
                  placement="top"
                  v-if="formInputs.locations.length > 5"
                >
                  <el-badge :value="formInputs.locations.length" class="item" type="success">
                    <ActionButton
                      label="Get Price Quotes"
                      loading-label="Getting prices…"
                      :state="loading ? 'loading' : 'idle'"
                      @click="getPriceQuote"
                    />
                  </el-badge>
                </el-tooltip>
                <ActionButton
                  v-else
                  label="Get Price Quotes"
                  loading-label="Getting prices…"
                  :state="loading ? 'loading' : 'idle'"
                  @click="getPriceQuote"
                />
              </el-col>
              <el-col :span="2"></el-col>
            </el-row>
            <el-row v-else>
              <el-col :span="2"></el-col>
              <el-col :span="20">
                <ActionButton label="Get Price Quotes" disabled />
              </el-col>
              <el-col :span="2"></el-col>
            </el-row>
          </div>
          <div v-else>
            <el-row>
              <el-col :span="2"></el-col>
              <el-col :span="20"><p class="cargo-size-heading"></p></el-col>
              <el-col :span="2"></el-col>
            </el-row>
            <el-row>
              <el-col :span="2"></el-col>
              <el-col :span="20">
                <div class="new-request-vendors-panel">
                  <el-tabs
                    v-model="activeCargoSizeHeaderName"
                    class="demo-tabs"
                    @tab-click="handleCargoSizeTab"
                    :stretch="tabsStretchSettings"
                  >
                    <el-tab-pane label="Small" name="first" v-if="hasSmallVendors">
                      <div
                        v-for="item in smallVendors"
                        class="request-vendor-row"
                        :class="{ 'request-vendor-row__active': isActiveRow(item.vehicle_type_id) }"
                        @click="selectQuote(item)"
                      >
                        <el-row>
                          <el-col :span="8"><el-icon size="large" class="request-vendor-row-icon"><Bicycle /></el-icon></el-col>
                          <el-col :span="8"><span class="request-vendor-row-label"><p>{{ item.vehicle_name }}</p></span></el-col>
                          <el-col :span="8"><span class="request-vendor-row-amount"><p>KES {{ item.cost.toLocaleString() }}</p></span></el-col>
                        </el-row>
                      </div>
                    </el-tab-pane>
                    <el-tab-pane label="Medium" name="second" v-if="hasMediumVendors">
                      <div
                        class="request-vendor-row"
                        :class="{ 'request-vendor-row__active': isActiveRow(item.vehicle_type_id) }"
                        @click="selectQuote(item)"
                        v-for="item in mediumVendors"
                      >
                        <el-row>
                          <el-col :span="8"><el-icon size="large" class="request-vendor-row-icon"><Van /></el-icon></el-col>
                          <el-col :span="8"><span class="request-vendor-row-label"><p>{{ item.vehicle_name }}</p></span></el-col>
                          <el-col :span="8"><span class="request-vendor-row-amount"><p>KES {{ item.cost.toLocaleString() }}</p></span></el-col>
                        </el-row>
                      </div>
                    </el-tab-pane>
                    <el-tab-pane label="Large" name="third" v-if="hasLargeVendors">
                      <div
                        class="request-vendor-row"
                        :class="{ 'request-vendor-row__active': isActiveRow(item.vehicle_type_id) }"
                        @click="selectQuote(item)"
                        v-for="item in largeVendors"
                      >
                        <el-row>
                          <el-col :span="8"><el-icon size="large" class="request-vendor-row-icon"><Van /></el-icon></el-col>
                          <el-col :span="8"><span class="request-vendor-row-label"><p>{{ item.vehicle_name }}</p></span></el-col>
                          <el-col :span="8"><span class="request-vendor-row-amount"><p>KES {{ item.cost.toLocaleString() }}</p></span></el-col>
                        </el-row>
                      </div>
                    </el-tab-pane>
                  </el-tabs>
                </div>
              </el-col>
              <el-col :span="2"></el-col>
            </el-row>
            <el-row>
              <el-col :span="2"></el-col>
              <el-col :span="20">
                <div style="margin: 10px 0" />
                <!-- What is being sent, the pickup time, stop contacts,
                     notes and the manifest are on the next step. -->
                <ActionButton
                  :label="continueLabel"
                  :disabled="!selectedQuote"
                  @click="openDetails"
                />
              </el-col>
              <el-col :span="2"></el-col>
            </el-row>
          </div>
        </div>
        </Transition>
        </div><!-- /stops and prices -->
        </div><!-- /v-show wrapper -->
      </div>
    </div>
    </div><!-- /outer-new-request-panel -->



 <!-- Floating action buttons — bottom right -->
    <div class="track-fab-group">
     
      <button class="track-fab track-fab--purple" @click="router.push('/transactions/orders')">
  <svg 
    width="18" 
    height="18" 
    viewBox="0 0 24 24" 
    fill="none" 
    stroke="currentColor" 
    stroke-width="2" 
    stroke-linecap="round" 
    stroke-linejoin="round" 
    style="margin-right: 8px; vertical-align: text-bottom;"
  >
    <path d="M4 2v20l2-1 2 1 2-1 2 1 2-1 2 1 2-1 2 1V2l-2 1-2-1-2 1-2-1-2 1-2-1-2 1-2-1Z" />
    <line x1="16" y1="8" x2="8" y2="8" />
    <line x1="16" y1="12" x2="8" y2="12" />
    <line x1="10" y1="16" x2="8" y2="16" />
  </svg>
  Order History
</button>
    </div>

    <GMapMap
      :center="mapCenter"
      :zoom="14"
      map-type-id="roadmap"
      :options="mapOptions"
      style="position:absolute; top:0; left:0; right:0; bottom:0; width:100%; height:100%;"
      ref="myMapRef"
    >
      <GMapMarker
        :key="index"
        v-for="(m, index) in formMarkers"
        :position="m.position"
        :clickable="false"
        :draggable="false"
        :icon="getMarkerIcon(index)"
      />
      <GMapPolyline
        v-if="routePath.length > 1"
        :path="routePath"
        :options="polylineOptions"
      />
    </GMapMap>
    <!-- Verification before ordering: shown while the gate blocks this
         account. Not dismissable — there is nothing to do here until the
         company is verified — but the rest of the app stays reachable. -->
    <div
      v-if="ordersBlocked"
      class="kyc-block"
      role="dialog"
      aria-modal="true"
      aria-labelledby="kyc-block-title"
    >
      <div class="kyc-block__card">
        <span class="kyc-block__icon" aria-hidden="true">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /><polyline points="9 12 11 14 15 10" /></svg>
        </span>
        <h2 id="kyc-block-title" class="kyc-block__title">Verify your company to place orders</h2>
        <p class="kyc-block__body">{{ blockCopy.body }}</p>
        <button v-if="blockIsAdmin" type="button" class="kyc-block__go" @click="router.push('/verify')">
          {{ blockCopy.action }}
        </button>
        <p class="kyc-block__hint">Your order history and account stay available from the menu.</p>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, ref, reactive, watch, nextTick, onMounted, onBeforeUnmount } from "vue";
import router from "../../router";
import {
  MapLocation,
  AddLocation,
  Van,
  Bicycle,
  Postcard,
  Delete,
  Edit,
} from "@element-plus/icons-vue";
import type { TabsPaneContext } from "element-plus";
import { ElMessage } from "element-plus";
declare const google: any;
import axios from "axios";
import { DateTime } from "luxon";
import { useAuthStore } from "../../store/authStore";
import AddressCombobox, { type SuggestedAddress } from "./AddressCombobox.vue";
import ActionButton from "./ActionButton.vue";
import OrderPlacedCard from "./OrderPlacedCard.vue";
import OrderTypeTabs from "./OrderTypeTabs.vue";
import OrderDetailsStep from "./OrderDetailsStep.vue";
import { type PickupMode, formatBookedFor } from "../../utils/orderTime";
import { ORDER_FEATURES, type OrderType } from "../../utils/orderFeatures";
import { combinePlaceName } from "../../utils/placesAutocomplete";
import { authHeaders, useCompanyKyc } from "../../composables/useCompanyKyc";
import { isCompanyAdminFromToken } from "../../utils/authRole";
import {
  type DraftLocation,
  type StopDetails,
  newDraftLocation,
  emptyStopDetails,
  toQuoteLocations,
  buildCreateOrderBody,
  createOrderErrorAction,
  cleanGoodsDescription,
  STOPS_MISMATCH_MESSAGE,
  placedOrderNotice,
} from "../../utils/orderPayload";
import { preferredDriverForOrder, preferredPlacedNotice } from "../../utils/preferredDriver";

const store = useAuthStore();
const token = localStorage.getItem("_panda");
axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;

const tabsStretchSettings = ref(true);
const isValidForm = ref(false);
const loading = ref(false);
const order_loading = ref(false);
// Set once the order is created. Drives the confirmation card, and the
// button's `done` state, and is the flag that stops placeOrder running twice.
const placedOrderNumber = ref<string | null>(null);
// From createOrder's data.warnings (utils/orderPayload placedOrderNotice).
// The card holds longer when there is one, so it can be read.
const placedNotice = ref<string | null>(null);
// A Dedicated booking's pickup ("Tue 4 Nov, 08:00") for the card; null for
// On-Demand. The card holds a little longer so it can be read.
const placedBookedFor = ref<string | null>(null);
const trackingTarget = ref<string | null>(null);
const showMinimisedBody = ref(true);

// Price quote limit (pricing helpers/priceQuoteLimit.js): each user (not the
// account as a whole) may request a number of price quotes in a rolling
// window, then a few more after a warning, then quoting pauses for that user
// until enough of their quotes have aged out. Quotes that become orders are
// given back.
//
// The window is a Xobo setting (1 to 168 hours, 24 unless changed). Pricing
// sends it with every quota and every refusal (window_hours); Account
// settings carries it too (price_quote_window_hours). 24 until one answers.
interface QuoteNotice { kind: "warn" | "blocked"; title: string; text: string }
const quoteNotice = ref<QuoteNotice | null>(null);
const quoteWindowHours = ref(24);

/** Keep a window the server sent, if it is one pricing would use. */
function takeQuoteWindow(hours: unknown) {
  const n = Number(hours);
  if (Number.isInteger(n) && n >= 1 && n <= 168) quoteWindowHours.value = n;
}

/** "24 hours", "1 hour", "2 days": the window as pricing words it. */
function quoteWindowText(): string {
  const h = quoteWindowHours.value;
  if (h > 24 && h % 24 === 0) return `${h / 24} days`;
  return `${h} hour${h === 1 ? "" : "s"}`;
}

const XOBO_ZONE = "Africa/Nairobi";

/**
 * "14:33", "14:33 tomorrow" or "14:33 on Sat 24 Oct", in Nairobi time as
 * pricing's own message says it. Rounded up to the minute, so coming back at
 * the time shown is never a minute early. The day is named once the window
 * can run past tomorrow.
 */
function resumeTime(iso: string | undefined): string {
  const exact = iso ? DateTime.fromISO(iso) : null;
  if (!exact || !exact.isValid) return "later";
  const up = exact.second || exact.millisecond ? exact.startOf("minute").plus({ minutes: 1 }) : exact;
  // hasSame compares wall-clock dates, so both sides go to Nairobi first.
  const at = up.setZone(XOBO_ZONE);
  const now = DateTime.now().setZone(XOBO_ZONE);
  const time = at.toFormat("HH:mm");
  if (at.hasSame(now, "day")) return time;
  if (at.hasSame(now.plus({ days: 1 }), "day")) return `${time} tomorrow`;
  return `${time} on ${at.toFormat("ccc d LLL")}`;
}

function noteQuota(quota: any) {
  if (!quota || !quota.warning) {
    quoteNotice.value = null;
    return;
  }
  takeQuoteWindow(quota.window_hours);
  const left = Number(quota.remaining) || 0;
  quoteNotice.value = left > 0
    ? {
        kind: "warn",
        title: "You're close to your price quote limit.",
        text: `You've had ${quota.used} price quote${Number(quota.used) === 1 ? "" : "s"} in the last ${quoteWindowText()}. You can get ${left} more before quoting pauses. Each person has their own allowance, so others on your account can still get prices. Quotes that become orders don't count.`,
      }
    : {
        kind: "warn",
        title: "That was your last price quote for now.",
        text: `Each person has their own allowance per ${quoteWindowText()}. You can get prices again at ${resumeTime(quota.resets_at)}. Placing an order with these prices still works, and quotes that become orders don't count.`,
      };
}

/** The 429 from pricing: say when this user can quote again. */
function noteQuoteRefusal(data: any) {
  takeQuoteWindow(data?.window_hours);
  const limit = Number(data?.limit);
  const grace = Number(data?.grace);
  const allowance = Number.isInteger(limit) && limit > 0
    ? `Each person has their own allowance: ${limit} price quote${limit === 1 ? "" : "s"} in ${quoteWindowText()}${Number.isInteger(grace) && grace > 0 ? `, plus ${grace} more` : ""}.`
    : `Each person has their own allowance of price quotes per ${quoteWindowText()}.`;
  quoteNotice.value = {
    kind: "blocked",
    title: "You've used all your price quotes for now.",
    text: `${allowance} You can get prices again at ${resumeTime(data?.resets_at)}. Quotes that become orders don't count.`,
  };
}

// Pickup plus drop-offs. The account's limit comes from Account > Settings
// (set by Xobo staff); pricing and orders refuse anything over it. 5 — the
// pickup and four drop-offs, the platform default — until it loads, and if
// it cannot be read.
//
// Staff can change it while the form is open, so it is read again before a
// stop is added and when the page comes back into view. Reading it only on
// load let a form opened at 10 keep adding stops after staff set 6, and the
// quote was then refused ("Remove 4 and try again").
const maxDeliveryLocations = ref(5);
const dedicatedOrdersSetting = ref(false);

// On-Demand unless the customer picks Dedicated, which needs both the
// feature and the company's setting.
const orderType = ref<OrderType>("on_demand");
const showDedicatedTab = computed(() => ORDER_FEATURES.dedicatedOrders && dedicatedOrdersSetting.value);
watch(showDedicatedTab, (show) => {
  if (!show && orderType.value === "dedicated") orderType.value = "on_demand";
});
// A quote is for one order type: switching keeps the stops and asks for a
// new price. Dedicated is Schedule only; back on On-Demand, a schedule
// without a time goes back to ASAP.
watch(orderType, (type) => {
  resetQuote();
  if (type === "dedicated") pickupMode.value = "scheduled";
  else if (pickupMode.value === "scheduled" && !pickupTime.value) pickupMode.value = "asap";
});
const loadLocationLimit = async () => {
  try {
    // Short timeout: Add Destination waits on this, and on a slow line the
    // last known limit is better than a button that seems to do nothing.
    const res = await axios.get(`${import.meta.env.VITE_ONBOARDING_SERVICE}account/settings`, {
      headers: authHeaders(),
      timeout: 3000,
    });
    const n = Number(res.data?.data?.max_locations);
    if (Number.isInteger(n) && n >= 2) maxDeliveryLocations.value = n;
    // Staff switch Dedicated bookings on per company.
    dedicatedOrdersSetting.value = res.data?.data?.dedicated_orders === true;
    // The price quote window, for the limit's messages (pricing's own
    // window_hours replaces it whenever a quote or a refusal carries one).
    takeQuoteWindow(res.data?.data?.price_quote_window_hours);
  } catch {
    // Keep the last known limit: the form must still work if it is unreadable.
  }
};
const refreshLimitWhenVisible = () => {
  if (document.visibilityState === "visible") void loadLocationLimit();
};
onMounted(() => document.addEventListener("visibilitychange", refreshLimitWhenVisible));
onBeforeUnmount(() => document.removeEventListener("visibilitychange", refreshLimitWhenVisible));
const activeRowIndex = ref(1000);
const formActiveVendor = ref("");
const formNotes = ref("");
const formManifestNumber = ref("");
const myMapRef = ref<any>(null);
const routePath = ref<{ lat: number; lng: number }[]>([]);
// True when the drawn line is NOT a driving route. Set when DirectionsService
// cannot route between the chosen points — most often because a picked result
// is a large administrative area (a county, say) whose centroid sits nowhere
// near a road, so Google returns ZERO_RESULTS.
const routeIsApproximate = ref(false);
const activeCargoSizeHeaderName = ref("first");
const pricingResponse = reactive({ data: <any>null });
const smallVendors = ref<vendorQuote[]>([]);
const mediumVendors = ref<vendorQuote[]>([]);
const largeVendors = ref<vendorQuote[]>([]);
const orderRequestInProgress = ref(false);

interface googleLocation {
  name: string;
  formatted_address?: string;
  lat: number;
  lng: number;
  place_id: string;
  key: number;
}

// Each stop also carries a stable uid and the receiver details captured in
// the Details step (utils/orderPayload). Neither goes to pricing.
interface formStruct {
  locations: DraftLocation[];
}

interface markerStruct {
  position: { lat: number; lng: number };
}

interface vendorQuote {
  vehicle_name: string;
  vehicle_type_id: number;
  cost: number;
  vehicle_size: string;
  // Sent by pricing from the loaders release: whether this vehicle's quote
  // offers loaders, at what rate per loader, and how many at most.
  loaders_available?: boolean;
  loader_unit_fee?: number | null;
  loaders_max?: number;
}

const formMarkers = reactive<markerStruct[]>([]);

const mapCenter = reactive<googleLocation>({
  name: "",
  key: 0,
  lat: -1.26723,
  lng: 36.81026,
  place_id: "",
});

const formInputs = reactive<formStruct>({
  locations: [newDraftLocation(0), newDraftLocation(1)],
});

const removeLocation = (index: number) => {
  formInputs.locations.splice(index, 1);
  removeMarker(index);
  occasionalFormValidityCheck();
  showMinimisedBody.value = true;
};

const removeMarker = (index: number) => {
  formMarkers.splice(index, 1);
};

// Back to the stops with no price on screen: the next step is Get Price
// Quotes. Stops and their details stay.
const resetQuote = () => {
  showMinimisedBody.value = true;
  clearSelection();
};

const isActiveRow = (index: number) => {
  return index == activeRowIndex.value;
};

// ─── Steps ──────────────────────────────────────────────────────────────
// Stops (showMinimisedBody) → prices → Continue → Details (detailsOpen).
// The chosen quote is kept whole: its vehicle, cost and, later, loaders.
const selectedQuote = ref<vendorQuote | null>(null);
// Loaders booked with this quote (0 = none); see Loaders below.
const loadersCount = ref(0);
// The driver's phone or plate the customer typed ("" = none); see
// Preferred driver below.
const preferredDriver = ref("");
const detailsOpen = ref(false);

const selectQuote = (quote: vendorQuote) => {
  selectedQuote.value = quote;
  activeRowIndex.value = quote.vehicle_type_id;
  formActiveVendor.value = quote.vehicle_name;
};

const clearSelection = () => {
  selectedQuote.value = null;
  activeRowIndex.value = 1000;
  formActiveVendor.value = "";
  loadersCount.value = 0;
  preferredDriver.value = "";
};

// ─── Loaders ────────────────────────────────────────────────────────────
// Offered when the chosen quote says so (pricing decides: medium and large
// vehicles with a rate set). The rate is the quote's; orders charges the
// same one, so the total below is what is billed.
const loadersOffer = computed(() => {
  const q = selectedQuote.value;
  if (!ORDER_FEATURES.loaders || !q || q.loaders_available !== true) return null;
  const unitFee = Number(q.loader_unit_fee);
  const max = Number(q.loaders_max);
  if (!(unitFee > 0) || !(max > 0)) return null;
  return { unitFee, max };
});
const loadersFee = computed(() => (loadersOffer.value ? loadersCount.value * loadersOffer.value.unitFee : 0));

// A different vehicle may not offer loaders, or fewer: keep the count valid.
watch(loadersOffer, (offer) => {
  if (!offer) loadersCount.value = 0;
  else if (loadersCount.value > offer.max) loadersCount.value = offer.max;
});

// ─── Preferred driver ───────────────────────────────────────────────────
// Whether a driver can be reserved depends on the vehicle, so a different
// vehicle starts the question again.
watch(() => selectedQuote.value?.vehicle_type_id, (now, before) => {
  if (now !== before) preferredDriver.value = "";
});

// Any change that sends the panel back to the stops also leaves Details.
watch(showMinimisedBody, (stops) => {
  if (stops) detailsOpen.value = false;
});

const orderTotal = computed(() => (Number(selectedQuote.value?.cost) || 0) + loadersFee.value);

const continueLabel = computed(() =>
  selectedQuote.value
    ? `Continue with ${selectedQuote.value.vehicle_name} · KES ${Number(selectedQuote.value.cost).toLocaleString()}`
    : "Continue",
);

const scrollPanelToTop = () => {
  nextTick(() => document.querySelector(".new-request-panel")?.scrollTo({ top: 0 }));
};

const openDetails = () => {
  if (!selectedQuote.value) return;
  detailsOpen.value = true;
  scrollPanelToTop();
};

// Edit stops: back to the stops with every stop's details kept (they live
// on the stop, by uid); the price and the choice go, so a new quote is needed.
const editStops = () => {
  resetQuote();
  scrollPanelToTop();
};

// ─── Details step values ────────────────────────────────────────────────
const GOODS_KEY = "xobo.order.lastGoods";
const readLastGoods = (): string => {
  try { return localStorage.getItem(GOODS_KEY) || ""; } catch { return ""; }
};
// Prefilled with this browser's last value: many customers send the same
// thing every time. It holds no personal data (drivers see it).
const goodsDescription = ref(readLastGoods());
const pickupMode = ref<PickupMode>("asap");
const pickupTime = ref("");

// Refreshed so "at least 15 minutes from now" stays true while the form
// is open.
const nowTick = ref(Date.now());
let nowTimer: ReturnType<typeof setInterval> | null = null;
onMounted(() => { nowTimer = setInterval(() => { nowTick.value = Date.now(); }, 30000); });
onBeforeUnmount(() => { if (nowTimer) clearInterval(nowTimer); });

// ─── Stop details ───────────────────────────────────────────────────────
// Edited inline on the Details step (StopDetailsEditor), saved here by uid.
const saveStopDetails = (uid: string, details: StopDetails) => {
  const stop = formInputs.locations.find((l) => l.uid === uid);
  if (stop) stop.details = { ...details };
};

// Put an already-resolved location into the form. Everything that used to
// live inside setPlace, minus the Google-shape adaptation — because a
// frequently-used address arrives with plain lat/lng numbers and needs no
// Google call at all. That split is what makes the suggestion path free.
const applyLocation = async (
  index: number,
  place: { name: string; formatted_address?: string; lat: number; lng: number; place_id?: string },
) => {
  try {
    // Re-picking an address keeps the stop's identity and its receiver
    // details: only the place changes.
    const previous = formInputs.locations[index];
    const location: DraftLocation = {
      uid: previous?.uid ?? newDraftLocation().uid,
      details: previous?.details ?? emptyStopDetails(),
      lat: Number(place.lat),
      lng: Number(place.lng),
      key: Date.now(),
      // The canonical string every downstream reader displays. Replayed
      // verbatim rather than re-derived, so the same physical address does not
      // split into two suggestions depending on how it was entered.
      name: place.name,
      formatted_address: place.formatted_address || "",
      place_id: place.place_id || "",
    };
    formInputs.locations.splice(index, 1, location);
    addMarker(location, index);
    await nextTick();
    occasionalFormValidityCheck();
    showMinimisedBody.value = true;
  } catch (er) {
    ElMessage({ message: "Failed to add the location, please retry", type: "error" });
  }
};

// Kept for any caller still handing us a raw Google place object: adapt, then
// delegate. combinePlaceName is the same helper the combobox uses, so both
// entry points produce byte-identical `name` values.
const setPlace = async (details: any, index: number) => {
  try {
    await applyLocation(index, {
      name: combinePlaceName(details.name || "", details.formatted_address || ""),
      formatted_address: details.formatted_address || "",
      lat: details.geometry.location.lat(),
      lng: details.geometry.location.lng(),
      place_id: details.place_id,
    });
  } catch (er) {
    ElMessage({ message: "Failed to add the location, please retry", type: "error" });
  }
};

const addMarker = (location: any, index: number) => {
  let { lat, lng } = location;
  formMarkers.splice(index, 1, { position: { lat, lng } });
};

const fetchDirectionsRoute = async () => {
  if (formMarkers.length < 2) { routePath.value = []; return; }
  try {
    const directionsService = new google.maps.DirectionsService();
    const waypoints = formMarkers.slice(1, -1).map((m: any) => ({
      location: m.position,
      stopover: true,
    }));
    const result = await directionsService.route({
      origin: formMarkers[0].position,
      destination: formMarkers[formMarkers.length - 1].position,
      waypoints,
      travelMode: google.maps.TravelMode.DRIVING,
      optimizeWaypoints: false,
    });
    // Decode all legs into a single flat path
    const path: { lat: number; lng: number }[] = [];
    result.routes[0].legs.forEach((leg: any) => {
      leg.steps.forEach((step: any) => {
        step.path.forEach((pt: any) => {
          path.push({ lat: pt.lat(), lng: pt.lng() });
        });
      });
    });
    routePath.value = path;
    routeIsApproximate.value = false;
  } catch (e) {
    // Fall back to joining the markers. Keep the line — it still shows which
    // points were chosen — but flag it so it renders dashed and the panel can
    // explain itself, rather than silently presenting a straight line across
    // country as though it were a road route.
    //
    // This matters beyond the map: the pricing service computes its own
    // distance the same way, and for these points it answers "Error in
    // processing distance data" with no quotes. So warning here is the only
    // chance to tell the customer something actionable before they hit an
    // opaque failure on Get Price Quotes.
    routePath.value = formMarkers.map((m: any) => m.position);
    routeIsApproximate.value = true;
  }
};

const updateBounds = () => {
  fetchDirectionsRoute();
  const mapInstance = myMapRef.value;
  try {
    if (mapInstance) {
      mapInstance.$mapPromise.then((map: any) => {
        var bounds = new google.maps.LatLngBounds();
        for (var i = 0; i < formMarkers.length; i++) {
          bounds.extend(formMarkers[i].position);
        }
        map.fitBounds(bounds, { left: 440, top: 40, right: 40, bottom: 40 });
      });
    } else {
      ElMessage({ message: "Failed to resize the map", type: "error" });
    }
  } catch (er) {
    console.log(er);
  }
};

const addingDestination = ref(false);
const addNewDestination = async () => {
  if (addingDestination.value) return;
  addingDestination.value = true;
  await loadLocationLimit();
  addingDestination.value = false;
  // The limit may have been lowered since the form opened: then the button
  // has gone and no stop is added.
  if (formInputs.locations.length >= maxDeliveryLocations.value) return;
  formInputs.locations.push(newDraftLocation());
  occasionalFormValidityCheck();
};

const getLocationDisplayText = (location: googleLocation): string => {
  // name already includes formatted_address details (combined when setting place)
  // So we just need to return name from backend
  return location.name || "";
};

const occasionalFormValidityCheck = () => {
  let emptyLocations = [];
  for (let i = 0; i < formInputs.locations.length; i++) {
    // A location is valid if it has a name (which now includes all details)
    if (formInputs.locations[i].name == "") {
      emptyLocations.push(i);
    }
  }
  if (emptyLocations.length > 0) {
    isValidForm.value = false;
    showMinimisedBody.value = true;
  } else {
    isValidForm.value = true;
  }
  updateBounds();
};

const handleCargoSizeTab = (tab: TabsPaneContext, event: Event) => {};

// autocompleteOptions is gone with the widget it configured. The equivalent
// settings now live in utils/placesAutocomplete: the Kenya restriction as
// REGION, and the field list at each resolve call — narrowed to the four
// fields actually used, since Places bills by field.

const isNewLocationPickupIcon = (index: number) => index == 0;
const isNewLocationDeliveryIcon = (index: number) => index != 0;

const setPlaceHolderName = (index: number) => {
  if (index == 0) return "Pick-up address";
  return "Drop-off address";
};

const displayDeleteIcon = (index: number) => {
  if (index < 2) return false;
  return true;
};

const allowNewLocation = computed(() => {
  return formInputs.locations.length < maxDeliveryLocations.value && isValidForm.value;
});

const getXVendors = (size: string) => {
  return pricingResponse.data.quotes.filter((quote: any) => quote.vehicle_size === size);
};

const hasSmallVendors = computed(() => {
  return pricingResponse.data.quotes.some((element: any) => element.vehicle_size == "small");
});

const hasMediumVendors = computed(() => {
  return pricingResponse.data.quotes.some((element: any) => element.vehicle_size == "medium");
});

const hasLargeVendors = computed(() => {
  return pricingResponse.data.quotes.some((element: any) => element.vehicle_size == "large");
});

const getPriceQuote = async () => {
  loading.value = true;
  try {
    const response = await axios.post(
      `${import.meta.env.VITE_PRICING_SERVICE}customerPriceCheck`,
      {
        company_id: store.getUserData.company_id,
        user_id: store.getUserData.user_id,
        // Only the six stop fields pricing has always had. Receiver details
        // go to createOrder after the vehicle is chosen, never here.
        locations: toQuoteLocations(formInputs.locations),
      },
      // The token tells pricing whose quote this is, for the price quote limit.
      { headers: authHeaders() }
    );
    if (response.status == 200) {
      if (response.data.status) {
        showMinimisedBody.value = false;
        pricingResponse.data = response.data.data;
        noteQuota(response.data.data?.quota);
        smallVendors.value = getXVendors("small");
        mediumVendors.value = getXVendors("medium");
        largeVendors.value = getXVendors("large");
        // A new price means a new choice.
        clearSelection();
        // Element Plus does not move off a tab that is not rendered, so
        // open on the first size that has quotes.
        activeCargoSizeHeaderName.value = smallVendors.value.length
          ? "first"
          : mediumVendors.value.length ? "second" : "third";
      } else {
        // Previous message: literal "response.data.message 1" — a debug
        // placeholder that shipped to customers. Interpolate the actual
        // backend reason (typically "No pricing configurations found …"
        // for an unsupported area), fall back to a helpful default.
        // Collapse the vendor panel so a stale prior response isn't left
        // sitting on screen next to the error.
        showMinimisedBody.value = true;
        ElMessage({
          message: response.data.message || "We couldn't get prices for that route. Please try different addresses.",
          type: "error",
        });
      }
    } else {
      showMinimisedBody.value = true;
      ElMessage({
        message: response.data?.message || "Pricing service is unreachable. Please try again shortly.",
        type: "error",
      });
    }
    loading.value = false;
  } catch (er: any) {
    console.log(er);
    loading.value = false;
    showMinimisedBody.value = true;
    // axios error path — dig for a backend-provided reason before
    // falling back to a generic message. The empty-vendors state
    // (rare — usually surfaces as {status:false} above) also lands here.
    const backendReason = er?.response?.data?.message || er?.response?.data?.reason;
    // Over the price quote limit: say when quoting resumes, in place of a toast.
    if (er?.response?.data?.code === "price_quote_limit") {
      noteQuoteRefusal(er.response.data.data);
      return;
    }
    // Refused for too many drop-offs: the limit changed while the form was
    // open. Take the real one so Add Destination stays hidden from now on.
    const refusedLimit = Number(er?.response?.data?.data?.max_dropoffs);
    if (er?.response?.data?.code === "too_many_dropoffs" && Number.isInteger(refusedLimit) && refusedLimit >= 1) {
      maxDeliveryLocations.value = refusedLimit + 1;
    }
    ElMessage({
      message: backendReason || "We couldn't get prices right now. Please try again.",
      type: "error",
    });
  }
};

const placeOrder = async () => {
  if (orderRequestInProgress.value) {
    ElMessage({ message: "Order is already being placed. Please wait...", type: "info" });
    return;
  }
  if (!selectedQuote.value) return;
  orderRequestInProgress.value = true;
  order_loading.value = true;
  try {
    // The whole body is built in utils/orderPayload.
    const response = await axios.post(
      `${import.meta.env.VITE_ORDERS_SERVICE}createOrder`,
      buildCreateOrderBody({
        token: store.getToken,
        company_id: store.getUserData.company_id,
        user_id: store.getUserData.user_id,
        pricing_uuid: pricingResponse.data.uuid,
        vehicle_type_id: selectedQuote.value.vehicle_type_id,
        order_type: orderType.value,
        pickup_mode: pickupMode.value,
        pickup_time: pickupTime.value,
        goods_description: goodsDescription.value,
        loaders_count: loadersOffer.value ? loadersCount.value : 0,
        preferred_driver: ORDER_FEATURES.preferredDriver ? preferredDriverForOrder(preferredDriver.value) : null,
        notes: formNotes.value,
        manifest: formManifestNumber.value,
        locations: formInputs.locations,
      }),
    );
    if (response.status == 200) {
      if (response.data.status) {
        try { localStorage.setItem(GOODS_KEY, cleanGoodsDescription(goodsDescription.value)); } catch { /* optional */ }
        const { tracking_token, order_number } = response.data.data;
        const trackQuery = tracking_token
          ? `?token=${tracking_token}`
          : `?order=${order_number}`;
        // Don't navigate yet. Show the order number first — under the old
        // flow it was created, used to build this URL, and never displayed.
        // OrderPlacedCard holds for a beat and then calls goToTracking.
        trackingTarget.value = `/track${trackQuery}`;
        order_loading.value = false;
        // Both can apply: a warning about what wasn't kept, and the driver
        // who couldn't be reserved.
        placedNotice.value = [
          placedOrderNotice(response.data.data?.warnings),
          preferredPlacedNotice(response.data.data?.preferred_driver, orderType.value === "dedicated"),
        ].filter(Boolean).join(" ") || null;
        placedBookedFor.value = orderType.value === "dedicated" ? formatBookedFor(pickupTime.value) || null : null;
        placedOrderNumber.value = order_number;
      } else {
        handlePlaceOrderRefusal(response.data);
      }
    } else {
      handlePlaceOrderFailure(response.data?.message);
    }
  } catch (er) {
    console.log(er);
    order_loading.value = false;
    // Session expired mid-checkout — auto-kick to login instead of
    // showing "Something went wrong" (which is misleading and leaves
    // the customer wondering whether their order actually went through).
    const errResp = (er as any)?.response?.data;
    const isAuthFail = (er as any)?.response?.status === 401;
    if (isAuthFail) {
      const msg = errResp?.code === 'token_expired'
        ? 'Your session expired. Please log in again to place this order.'
        : errResp?.message || 'Please log in again to place this order.';
      ElMessage({ message: msg, type: 'warning' });
      store.setAuthStatus(false);
      store.$reset();
      localStorage.removeItem('_panda');
      router.push('/login');
    } else {
      handlePlaceOrderRefusal(errResp);
      // Refused by the verification gate: refresh the account's status so
      // the notice explaining it, with the way to fix it, appears.
      if (errResp?.code === "kyc_required") void kyc.load(true);
    }
  } finally {
    order_loading.value = false;
    orderRequestInProgress.value = false;
  }
};

// A refusal with a code orders gives for the new fields:
// - fixable on the Details step (goods, a contact phone, the time): say why
//   and keep the quote;
// - stops_mismatch: the stops no longer match the price, so get a new one;
// - anything else: the reset below.
function handlePlaceOrderRefusal(data: any) {
  const action = createOrderErrorAction(data?.code);
  if (action === "details") {
    order_loading.value = false;
    const msg = typeof data?.message === "string" && data.message.trim()
      ? data.message
      : "Check the order details and try again.";
    ElMessage({ message: msg, type: "error", duration: 5000 });
    // Dedicated was switched off for this account while the form was open:
    // re-read the settings so the tab goes and the form falls back to
    // On-Demand, instead of failing the same way on every retry.
    if (data?.code === "dedicated_not_enabled") void loadLocationLimit();
    return;
  }
  if (action === "requote" && data?.code === "stops_mismatch") {
    handlePlaceOrderFailure(STOPS_MISMATCH_MESSAGE);
    return;
  }
  handlePlaceOrderFailure(data?.message);
}

// R5 fix: when placeOrder fails for a non-auth reason (typically an
// expired pricing_uuid — pricing service TTLs the quote and rejects
// createOrder if the customer sat on the vendor panel too long) the
// prior UX left the vendor panel expanded with the stale quote still
// selected, no signal to the customer that the fix is to re-quote.
// Collapse the panel and null the pricing state so the very next
// action they can take is "Get Price Quotes" again.
function handlePlaceOrderFailure(backendMsg: unknown) {
  order_loading.value = false;
  const msg = typeof backendMsg === 'string' && backendMsg.trim()
    ? backendMsg
    : 'We could not place your order. Please re-check your pickup + dropoff and get a fresh price quote.';
  ElMessage({ message: msg, type: 'error', duration: 5000 });
  // Reset the vendor panel state so a stale expired quote isn't left
  // sitting under the button. Types mirror the initial values declared
  // at the top of <script setup> — 1000 for activeRowIndex (used as
  // the "nothing selected" sentinel elsewhere) and '' for
  // formActiveVendor (its ref<string> shape).
  showMinimisedBody.value = true;
  pricingResponse.data = { quotes: [], uuid: null };
  smallVendors.value = [];
  mediumVendors.value = [];
  largeVendors.value = [];
  clearSelection();
}

const logwat = () => {
  store.setAuthStatus(false);
  store.$reset();
  localStorage.removeItem("_panda");
  router.push("/login");
};

const mapOptions = {
  styles: [
    { elementType: "geometry", stylers: [{ color: "#eaf0fb" }] },
    { elementType: "labels.icon", stylers: [{ visibility: "off" }] },
    { elementType: "labels.text.fill", stylers: [{ color: "#3d4a5c" }] },
    { elementType: "labels.text.stroke", stylers: [{ color: "#ffffff" }] },
    { featureType: "administrative", elementType: "geometry", stylers: [{ visibility: "off" }] },
    { featureType: "administrative.land_parcel", elementType: "labels", stylers: [{ visibility: "off" }] },
    { featureType: "poi", stylers: [{ visibility: "off" }] },
    { featureType: "poi.park", elementType: "geometry", stylers: [{ color: "#c8e6c9" }] },
    { featureType: "poi.park", elementType: "labels.text.fill", stylers: [{ color: "#388e3c" }] },
    { featureType: "road", elementType: "geometry", stylers: [{ color: "#ffffff" }] },
    { featureType: "road", elementType: "geometry.stroke", stylers: [{ color: "#dce6f5" }] },
    { featureType: "road", elementType: "labels.icon", stylers: [{ visibility: "off" }] },
    { featureType: "road.arterial", elementType: "geometry", stylers: [{ color: "#ffffff" }] },
    { featureType: "road.highway", elementType: "geometry", stylers: [{ color: "#b8cef5" }] },
    { featureType: "road.highway", elementType: "geometry.stroke", stylers: [{ color: "#9ab8e8" }] },
    { featureType: "road.highway", elementType: "labels.text.fill", stylers: [{ color: "#12284A" }] },
    { featureType: "road.local", stylers: [{ visibility: "simplified" }] },
    { featureType: "transit", stylers: [{ visibility: "off" }] },
    { featureType: "water", elementType: "geometry", stylers: [{ color: "#a8cce0" }] },
    { featureType: "water", elementType: "labels.text.fill", stylers: [{ color: "#3d6b8a" }] },
  ],
  disableDefaultUI: true,
  zoomControl: false,
};

const MARKER_COLORS = ['#22c55e', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4'];

// Two looks, because the line means two different things. A solid line is a
// real driving route from DirectionsService. When that returns ZERO_RESULTS
// the code falls back to joining the markers, which is a straight line across
// country — drawing THAT solid claimed a road route that does not exist.
const ROUTE_SOLID = {
  strokeColor: "#2563eb",
  strokeOpacity: 1.0,
  strokeWeight: 5,
  geodesic: true,
};
const ROUTE_APPROXIMATE = {
  strokeColor: "#94a3b8",
  // Dashes are drawn as repeated icons; the base stroke must be invisible.
  strokeOpacity: 0,
  strokeWeight: 4,
  geodesic: true,
  icons: [{
    icon: { path: "M 0,-1 0,1", strokeOpacity: 0.9, strokeWeight: 4, scale: 3 },
    offset: "0",
    repeat: "16px",
  }],
};
const polylineOptions = computed(() => (
  routeIsApproximate.value ? ROUTE_APPROXIMATE : ROUTE_SOLID
));

const getMarkerIcon = (index: number) => {
  const color = MARKER_COLORS[index % MARKER_COLORS.length];
  const label = String.fromCharCode(65 + index);
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="36" height="44" viewBox="0 0 36 44">
      <filter id="shadow${index}" x="-30%" y="-30%" width="160%" height="160%">
        <feDropShadow dx="0" dy="2" stdDeviation="2" flood-color="rgba(0,0,0,0.25)"/>
      </filter>
      <ellipse cx="18" cy="42" rx="6" ry="2.5" fill="rgba(0,0,0,0.12)"/>
      <path d="M18 2C10.268 2 4 8.268 4 16c0 10 14 26 14 26s14-16 14-26C32 8.268 25.732 2 18 2z" fill="${color}" filter="url(#shadow${index})"/>
      <circle cx="18" cy="16" r="8" fill="white" opacity="0.92"/>
      <text x="18" y="20" text-anchor="middle" font-size="9" font-weight="700" font-family="DM Sans, sans-serif" fill="${color}">${label}</text>
    </svg>`;
  return {
    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
    scaledSize: { width: 36, height: 44 },
    anchor: { x: 18, y: 44 },
  };
};

// Called by OrderPlacedCard when its hold elapses. Navigation lives here
// rather than in the card so a refused route cannot strand the customer
// inside a component whose only job is to wait.
const goToTracking = () => {
  if (trackingTarget.value) router.push(trackingTarget.value);
};

// ─── Frequently-used addresses ─────────────────────────────────────────
// Derived server-side from this company's own order history, with favourites
// pinned on top. Fetched once when the form mounts: there are at most five
// stops and the list cannot change mid-booking, so re-fetching per stop would
// be pure waste.
//
// A failure here is deliberately silent. Suggestions are a convenience; a
// customer who cannot load them can still type an address exactly as before,
// and an error toast for something they never asked for would be noise.
const addressSuggestions = ref<SuggestedAddress[]>([]);

const loadAddressSuggestions = async () => {
  try {
    const { data } = await axios.get(
      `${import.meta.env.VITE_ORDERS_SERVICE}addresses/frequent`,
      { params: { limit: 40 } },
    );
    addressSuggestions.value = (data?.data?.addresses || []) as SuggestedAddress[];
  } catch (er) {
    addressSuggestions.value = [];
  }
};

// Pin / unpin. Applied optimistically so the star responds immediately, then
// reconciled — on failure the local flag goes back rather than being left
// showing a state that was never saved.
const pinAddress = async (address: SuggestedAddress, makeFavourite: boolean) => {
  const row = addressSuggestions.value.find((a) => a.identity_key === address.identity_key);
  const previous = row ? row.is_favourite : !makeFavourite;
  if (row) row.is_favourite = makeFavourite;
  try {
    const url = `${import.meta.env.VITE_ORDERS_SERVICE}addresses/favourite`;
    const body = {
      address: {
        identity_key: address.identity_key,
        place_id: address.place_id,
        name: address.name,
        formatted_address: address.formatted_address,
        lat: address.lat,
        lng: address.lng,
      },
    };
    if (makeFavourite) await axios.post(url, body);
    // DELETE carries the identity in its body: a favourite is addressed by
    // (company, identity_key), not by a surrogate id the client was told.
    else await axios.delete(url, { data: body });
  } catch (er: any) {
    if (row) row.is_favourite = previous;
    ElMessage({
      message: er?.response?.data?.message || "Could not update that favourite",
      type: "warning",
    });
  }
};

onMounted(loadAddressSuggestions);
onMounted(loadLocationLimit);

// ─── Verification before ordering ────────────────────────────────────
// When Xobo has switched the order gate on, a business account that is not
// verified yet cannot order (orders refuses createOrder). Say so before the
// customer builds an order, not when it fails at the end.
const kyc = useCompanyKyc();
onMounted(() => void kyc.load(true));
const ordersBlocked = computed(() => !!kyc.view.value?.order_gate?.blocks_orders);
const blockIsAdmin = isCompanyAdminFromToken(localStorage.getItem("_panda"));
const blockCopy = computed(() => {
  const status = kyc.status.value;
  if (status === "under_review") {
    return {
      body: "We're reviewing your company documents. You can place orders as soon as they are approved, and we'll email your admins when that happens.",
      action: "View verification",
    };
  }
  if (status === "rejected") {
    return {
      body: blockIsAdmin
        ? "Some of your company documents need replacing. Replace them and we'll review them again; you can order once they are approved."
        : "Some of your company documents need replacing. An admin of your account can replace them; you can order once they are approved.",
      action: "Replace documents",
    };
  }
  return {
    body: blockIsAdmin
      ? "Business accounts are verified before their first order. Upload your company documents and we'll review them."
      : "Business accounts are verified before their first order. An admin of your account needs to upload the company documents.",
    action: "Upload documents",
  };
});
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600&display=swap');

.ep-main, .el-main {
  padding: 0 !important;
  position: relative;
  height: 100%;
  width: 100%;
  flex: 1;
  font-family: 'DM Sans', sans-serif;
}

/* Hold-until-approve reassurance shown above the Place-Order button.
   Violet tone (matches track-hold-note on the Track page) so both
   surfaces read as one voice. */
.nr-hold-note {
  margin: 0 0 8px;
  padding: 8px 12px;
  border-radius: 6px;
  background: #f5f3ff;
  border: 1px solid #ddd6fe;
  color: #5b21b6;
  font-size: 0.78rem;
  font-style: italic;
  line-height: 1.4;
  text-align: center;
}

/* ── Outer container ──────────────────────── */
.outer-new-request-panel {
  position: absolute;
  top: 0; left: 0; right: 0; bottom: 0;
  /*max-width: 1280px; */
  margin: 0 auto;
  padding: 0 30px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  pointer-events: none;
  z-index: 10;
}

/* ── Panel ────────────────────────────────── */
.new-request-panel {
  position: absolute;
  top: 24px;
  width: 420px;
  max-height: calc(100vh - 110px);
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 8px 40px rgba(18,40,74,0.16), 0 2px 8px rgba(18,40,74,0.08);
  z-index: 10;
  overflow-y: auto;
  overflow-x: hidden;
  scrollbar-width: none;
  pointer-events: all;
  transition: max-height 0.35s cubic-bezier(0.4,0,0.2,1);
}
.new-request-panel::-webkit-scrollbar { display: none; }
.new-request-panel-content { padding: 0; }

/* ── Panel header section (locations) ────── */
.new-request-head {
  background: linear-gradient(135deg, #12284A 0%, #1e3f73 100%);
  padding: 26px 4px;
  border-radius: 16px 16px 0 0;
}

/* ── Panel body ───────────────────────────── */
.new-request-body-section {
  padding: 10px 9px 35px;
  background: #f8fafc;
}

/* Shown when the drawn line is a straight fallback, not a road route. Amber
   rather than red: the customer has not done anything wrong, and they can
   still try for a quote — it just probably will not succeed. */
.quote-limit-note {
  margin: 2px 0 10px;
  padding: 9px 11px;
  border-radius: 8px;
  font-family: 'DM Sans', sans-serif;
  font-size: 0.74rem;
  line-height: 1.5;
}
.quote-limit-note strong {
  display: block;
  font-weight: 600;
}
.quote-limit-note.is-warn {
  background: #fff8ed;
  border: 1px solid #fcd9a4;
  color: #8a5200;
}
.quote-limit-note.is-blocked {
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #991b1b;
}

.route-approx-note {
  margin: 2px 0 10px;
  padding: 9px 11px;
  border-radius: 8px;
  background: #fff8ed;
  border: 1px solid #fcd9a4;
  color: #8a5200;
  font-family: 'DM Sans', sans-serif;
  font-size: 0.74rem;
  line-height: 1.5;
}

/* ── Panel expand/collapse transition ─────── */
.panel-slide-enter-active, .panel-slide-leave-active {
  transition: all 0.32s cubic-bezier(0.4, 0, 0.2, 1);
  overflow: hidden;
}
.panel-slide-enter-from, .panel-slide-leave-to {
  opacity: 0; max-height: 0; transform: translateY(-6px);
}
.panel-slide-enter-to, .panel-slide-leave-from {
  opacity: 1; max-height: 600px; transform: translateY(0);
}

/* ── Location inputs ──────────────────────── */
/* :deep() because the input now lives INSIDE AddressCombobox rather than
   being the child component's root element. A parent's scoped styles only
   reach a child's root node, so `.request-stop[data-v-parent]` stopped
   matching the moment the input gained a wrapper — which is what left these
   fields rendering as unstyled default text boxes. */
:deep(.request-stop) {
  width: 100%;
  height: 40px;
  border-radius: 8px;
  border: 1.5px solid rgba(255,255,255,0.2);
  background: rgba(255,255,255,0.1);
  color: #ffffff;
  padding: 0 10px;
  font-family: 'DM Sans', sans-serif;
  font-size: 0.84rem;
  outline: none;
  transition: border-color 0.15s, background 0.15s;
  box-sizing: border-box;
}
:deep(.request-stop::placeholder) { color: rgba(255,255,255,0.45); }
:deep(.request-stop:focus) {
  border-color: rgba(255,255,255,0.55);
  background: rgba(255,255,255,0.16);
  outline: none;
}

.new-request-location-icon {
  height: -webkit-fill-available;
  color: rgba(255,255,255,0.45);
}

.new-request-location-icon__actionable {
  cursor: pointer;
  color: rgba(255, 255, 255, 0.45);
  background: rgba(255,255,255,0.08);
  border-radius: 50%;
  width: 26px; height: 26px;
  display: flex; align-items: center; justify-content: center;
  margin: auto;
  transition: background 0.15s, color 0.15s;
  margin-top: 7px;
}
.new-request-location-icon__actionable:hover {
  color: #ff6b6b;
  background: rgba(255, 100, 100, 0.15);
}

.new-request-location-row-breather { margin: 6px 0; }
.new-request-location-row-breather__minimal { margin: 4px; }

.new-request-add-destination-button { float: left; cursor: pointer; }
.add-destination {
  color: rgba(255,255,255,0.75);
  font-weight: 500; font-size: 0.8rem;
  text-decoration: none;
  display: flex; align-items: center; gap: 6px;
  padding: 4px 0;
  transition: color 0.15s;
}
.add-destination:hover { color: white; }
.kyc-block {
  position: absolute;
  inset: 0;
  z-index: 20;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  background: rgba(15, 23, 42, 0.35);
  backdrop-filter: blur(2px);
}
.kyc-block__card {
  width: 100%;
  max-width: 420px;
  padding: 28px 28px 24px;
  border-radius: 16px;
  background: #fff;
  box-shadow: 0 24px 48px -16px rgba(18, 40, 74, 0.45);
  font-family: "DM Sans", sans-serif;
  text-align: center;
}
.kyc-block__icon {
  display: inline-flex;
  width: 48px;
  height: 48px;
  align-items: center;
  justify-content: center;
  border-radius: 14px;
  background: #fffbeb;
  color: #b45309;
}
.kyc-block__title {
  margin: 14px 0 6px;
  font-size: 1.1rem;
  font-weight: 700;
  color: #12284a;
}
.kyc-block__body {
  margin: 0 0 18px;
  font-size: 0.86rem;
  line-height: 1.5;
  color: #475569;
}
.kyc-block__go {
  width: 100%;
  height: 44px;
  border: none;
  border-radius: 10px;
  background: #12284a;
  color: #fff;
  font-family: inherit;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
}
.kyc-block__go:hover { background: #1e3f73; }
.kyc-block__go:focus-visible { outline: 2px solid #12284a; outline-offset: 2px; }
.kyc-block__hint {
  margin: 12px 0 0;
  font-size: 0.75rem;
  color: #94a3b8;
}
.new-request-pickup-location-icon { color: rgba(255,255,255,0.6); }
.new-request-delivery-location-icon { color: #86efac; }

/* ── Divider ──────────────────────────────── */
:deep(.ep-divider), :deep(.el-divider) {
  margin: 0 !important;
  border-color: rgba(255,255,255,0.08) !important;
}

/* ── Get quotes / Place order buttons ────── */
/* .new-request-full-button is gone: ActionButton carries these styles itself
   rather than depending on a parent's scoped rule reaching into a child. */
/* The .ep-loading-mask / .ep-loading-spinner overrides that used to live here
   are gone with v-loading. They existed to make a container mask look
   acceptable on top of a button, which was the wrong primitive — ActionButton
   renders an inline spinner instead. */

/* ── Tabs ─────────────────────────────────── */
:deep(.ep-tabs__header), :deep(.el-tabs__header) { margin-bottom: 10px !important; }
:deep(.ep-tabs__item), :deep(.el-tabs__item) {
  font-family: 'DM Sans', sans-serif !important;
  font-size: 0.82rem !important; font-weight: 500 !important;
  color: #9ca3af !important; padding: 0 12px !important;
}
:deep(.ep-tabs__item.is-active), :deep(.el-tabs__item.is-active) { color: #12284A !important; }
:deep(.ep-tabs__active-bar), :deep(.el-tabs__active-bar) { background-color: #12284A !important; }
:deep(.ep-tabs__nav-wrap::after), :deep(.el-tabs__nav-wrap::after) {
  background-color: #e5e7eb !important; height: 1px !important;
}

/* ── Vendor rows ──────────────────────────── */
.new-request-vendors-panel { margin-bottom: 4px; }
.request-vendor-row {
  display: flex; align-items: center; justify-content: space-between;
  padding: 10px 14px;
  border: 1.5px solid #e5e7eb;
  border-radius: 10px;
  cursor: pointer; margin-bottom: 6px;
  transition: border-color 0.15s, background 0.1s, box-shadow 0.15s;
  background: white;
}
.request-vendor-row:hover {
  border-color: #12284A; background: #f8fafc;
  box-shadow: 0 2px 8px rgba(18,40,74,0.08);
}
.request-vendor-row__active {
  border-color: #12284A !important;
  background: linear-gradient(135deg, #eef2f8 0%, #e8eef7 100%) !important;
  box-shadow: 0 2px 10px rgba(18,40,74,0.1) !important;
}
.request-vendor-row :deep(.ep-row), .request-vendor-row :deep(.el-row) { width: 100%; align-items: center; }
.request-vendor-row div { height: auto !important; }
.request-vendor-row-icon { display: flex; align-items: center; color: #6b7280; }
.request-vendor-row-label { font-size: 0.84rem; font-weight: 500; color: #374151; text-align: center; }
.request-vendor-row-label p { margin: 0; padding: 0; }
.request-vendor-row-amount { font-size: 0.84rem; font-weight: 700; color: #12284A; text-align: right; }
.request-vendor-row-amount p { margin: 0; padding: 0; }

/* ── Notes / Manifest / Schedule ─────────── */
.cargo-size-heading {
  font-size: 0.68rem; color: #9ca3af; margin: 0;
  text-transform: uppercase; letter-spacing: 0.05em;
}
.request-form-notes { width: 100%; }
:deep(.request-form-notes .ep-textarea__inner),
:deep(.request-form-notes .el-textarea__inner) {
  border-radius: 10px !important; border: 1.5px solid #e5e7eb !important;
  font-family: 'DM Sans', sans-serif !important; font-size: 0.84rem !important;
  color: #374151 !important; background: white !important;
  padding: 10px 12px !important; resize: none; box-shadow: none !important;
  transition: border-color 0.15s !important;
}
:deep(.request-form-notes .ep-textarea__inner:focus),
:deep(.request-form-notes .el-textarea__inner:focus) { border-color: #12284A !important; }
:deep(.request-form-notes .ep-input__wrapper),
:deep(.request-form-notes .el-input__wrapper) {
  border-radius: 10px !important; border: 1.5px solid #e5e7eb !important;
  background: white !important; box-shadow: none !important;
  padding: 0 12px !important; height: 42px !important;
}
:deep(.request-form-notes .ep-input__wrapper:focus-within),
:deep(.request-form-notes .el-input__wrapper:focus-within) {
  border-color: #12284A !important; box-shadow: none !important;
}
:deep(.request-form-notes .ep-input__inner),
:deep(.request-form-notes .el-input__inner) {
  font-family: 'DM Sans', sans-serif !important; font-size: 0.84rem !important; color: #374151 !important;
}

.request-form-schedule-label {
  display: block; font-size: 0.75rem; font-weight: 600; color: #6b7280;
  text-transform: uppercase; letter-spacing: 0.06em; margin-bottom: 8px;
}
.request-form-schedule-date { width: 100%; }
:deep(.request-form-schedule-date .ep-input__wrapper),
:deep(.request-form-schedule-date .el-input__wrapper),
:deep(.request-form-schedule-date .ep-input__wrapper:focus-within),
:deep(.request-form-schedule-date .el-input__wrapper:focus-within),
:deep(.request-form-schedule-date .ep-input__wrapper.is-focus),
:deep(.request-form-schedule-date .el-input__wrapper.is-focus) {
  border-radius: 10px !important;
  border: 1.5px solid #e5e7eb !important;
  background: white !important;
  box-shadow: none !important;
  outline: none !important;
  height: 42px !important;
  font-family: 'DM Sans', sans-serif !important;
}
:deep(.request-form-schedule-date .ep-input__prefix-inner),
:deep(.request-form-schedule-date .el-input__prefix-inner) { color: #9ca3af !important; }
:deep(.request-form-schedule-date .ep-input__inner),
:deep(.request-form-schedule-date .el-input__inner) {
  font-family: 'DM Sans', sans-serif !important;
  font-size: 0.84rem !important;
  color: #374151 !important;
}

/* ── Place order button ───────────────────── */
:deep(.ep-button--primary), :deep(.el-button--primary) {
  background: linear-gradient(135deg, #12284A 0%, #1e3f73 100%) !important;
  border-color: transparent !important; font-weight: 600 !important;
  letter-spacing: 0.01em !important; height: 44px !important;
  border-radius: 10px !important; font-family: 'DM Sans', sans-serif !important;
  width: 100% !important;
}
:deep(.ep-button--primary:hover), :deep(.el-button--primary:hover) {
  background: linear-gradient(135deg, #1a3a6a 0%, #2a5299 100%) !important;
}
/* ── Floating action buttons ──────────────── */
.track-fab-group {
  position: absolute;
  bottom: 100px;
  right: 32px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  z-index: 10;
  pointer-events: all;
}
.track-fab {
  height: 44px;
  padding: 0 22px;
  border-radius: 22px;
  font-family: 'DM Sans', sans-serif;
  font-size: 0.84rem;
  font-weight: 700;
  cursor: pointer;
  border: none;
  transition: all 0.18s cubic-bezier(0.4,0,0.2,1);
  letter-spacing: 0.02em;
  white-space: nowrap;
  box-shadow: 0 4px 16px rgba(0,0,0,0.15), 0 1.5px 4px rgba(0,0,0,0.08);
  width:200px;
}
.track-fab:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(0,0,0,0.2), 0 2px 6px rgba(0,0,0,0.1);
}
.track-fab:active { transform: translateY(0); }
.track-fab--green {
  background: linear-gradient(135deg, #16a34a 0%, #22c55e 100%);
  color: white;
}
.track-fab--purple {
  background: linear-gradient(135deg, #7c3aed 0%, #8b5cf6 100%);
  color: white;
}

/* ── Phones ───────────────────────────────── */
/* The panel was a fixed 420 px card, wider than a phone. Below 480 px it
   spans the screen with a 12 px gutter, and stops short of the Order
   History button, which moves to the bottom corner. */
@media (max-width: 480px) {
  .outer-new-request-panel { padding: 0; }
  .new-request-panel {
    left: 12px;
    right: 12px;
    top: 12px !important;
    width: auto;
    max-height: calc(100vh - 170px);
  }
  .track-fab-group { bottom: 16px; right: 16px; }
  .track-fab { width: auto; }
}
</style>